import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Grid,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as VerifiedIcon,
} from "@mui/icons-material";
import { toast } from "../../../../Components/ui";
import { colors, borderRadiusPx } from "../../../../../theme";
import {
  getExplorerAddressUrl,
  EXPLORER_LABEL,
} from "../../../../../static/Constants";

const LotteryWinnerCard = ({
  winnerAddress,
  winningNumber,
  totalPrizeInCurrency,
  currency,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!winnerAddress) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(winnerAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Winner address copied!");
    }
  };

  const explorerUrl = getExplorerAddressUrl(winnerAddress);

  // Parse winning numbers (if hyphenated, array, or string)
  const winningNumbersList = React.useMemo(() => {
    if (!winningNumber) return [];
    if (Array.isArray(winningNumber)) return winningNumber;
    const str = String(winningNumber).trim();
    if (str.includes("-")) {
      return str.split("-").filter(Boolean);
    }
    if (str.includes(",")) {
      return str.split(",").filter(Boolean);
    }
    if (str.length > 1 && !isNaN(Number(str))) {
      return str.split("");
    }
    return [str];
  }, [winningNumber]);

  return (
    <Card
      className="lottery-glass-card"
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.emerald[50],
        border: `1px solid ${colors.emerald[200]} !important`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Grid container spacing={3} alignItems="center">
        {/* Left: Trophy & Header */}
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: { xs: 54, sm: 64 },
                height: { xs: 54, sm: 64 },
                minWidth: { xs: 54, sm: 64 },
                borderRadius: "50%",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
              }}
            >
              <TrophyIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    color: colors.emerald[800],
                    letterSpacing: "0.2px",
                    fontSize: "1.1rem",
                  }}
                >
                  Official Winner Declared!
                </Typography>
                <VerifiedIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />
              </Stack>

              <Typography variant="body2" sx={{ color: colors.slate[700], mt: 0.25, mb: 1 }}>
                Winning prize of{" "}
                <strong style={{ color: colors.slate[900] }}>
                  {totalPrizeInCurrency} {currency}
                </strong>{" "}
                has been awarded on-chain.
              </Typography>

              {/* Winner Address */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 700,
                    color: colors.slate[900],
                    bgcolor: "#ffffff",
                    px: 1.25,
                    py: 0.5,
                    borderRadius: borderRadiusPx.sm,
                    border: `1px solid ${colors.emerald[300]}`,
                  }}
                >
                  {winnerAddress}
                </Typography>

                <Tooltip title={copied ? "Copied!" : "Copy Winner Address"}>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                      color: colors.slate[600],
                      bgcolor: "#ffffff",
                      border: `1px solid ${colors.emerald[200]}`,
                      "&:hover": { color: colors.slate[900], bgcolor: colors.emerald[100] },
                    }}
                  >
                    {copied ? (
                      <CheckIcon sx={{ fontSize: 16, color: colors.success.main }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>

                {explorerUrl && (
                  <Tooltip title={EXPLORER_LABEL}>
                    <IconButton
                      size="small"
                      component="a"
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: colors.slate[600],
                        bgcolor: "#ffffff",
                        border: `1px solid ${colors.emerald[200]}`,
                        "&:hover": { color: colors.emerald[700], bgcolor: colors.emerald[100] },
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Stack>
        </Grid>

        {/* Right: Winning Numbers Ball Display */}
        {winningNumbersList.length > 0 && (
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 2,
                borderRadius: borderRadiusPx.lg,
                bgcolor: "#ffffff",
                border: `1px solid ${colors.emerald[200]}`,
                textAlign: { xs: "left", md: "right" },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[600],
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  mb: 1,
                }}
              >
                Winning Combination
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                {winningNumbersList.map((num, idx) => (
                  <Box
                    key={idx}
                    className="lottery-ball-badge"
                    sx={{
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#ffffff",
                      border: "2px solid #34d399",
                    }}
                  >
                    {num}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

LotteryWinnerCard.propTypes = {
  winnerAddress: PropTypes.string.isRequired,
  winningNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.number]),
  totalPrizeInCurrency: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};

export default LotteryWinnerCard;
